const FBP_COOKIE = '_fbp';
const FBC_COOKIE = '_fbc';
const FBP_STORAGE_KEY = 'watchi_meta_fbp';
const FBC_STORAGE_KEY = 'watchi_meta_fbc';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

export interface MetaAttribution {
  fbp?: string;
  fbc?: string;
}

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;

  const prefix = `${name}=`;
  for (const cookie of document.cookie.split(';')) {
    const trimmedCookie = cookie.trim();
    if (!trimmedCookie.startsWith(prefix)) continue;

    const value = trimmedCookie.slice(prefix.length);
    try {
      return decodeURIComponent(value);
    } catch (_) {
      return value;
    }
  }

  return undefined;
}

function readStorage(key: string): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    return window.localStorage.getItem(key) || undefined;
  } catch (_) {
    return undefined;
  }
}

function writeStorage(key: string, value: string) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch (_) {
    // Cookies remain the primary storage when localStorage is unavailable.
  }
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
}

function isMetaBrowserId(value: string | undefined): value is string {
  return Boolean(value && value.length <= 500 && /^fb\.\d+\.\d+\..+$/i.test(value));
}

function getFbclid(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const value = new URLSearchParams(window.location.search).get('fbclid')?.trim();
    return value && value.length <= 400 ? value : undefined;
  } catch (_) {
    return undefined;
  }
}

function createRandomBrowserId(): string {
  let randomValue: number;
  try {
    const values = new Uint32Array(1);
    window.crypto.getRandomValues(values);
    randomValue = values[0];
  } catch (_) {
    randomValue = Math.floor(Math.random() * 0xffffffff);
  }

  return `fb.1.${Date.now()}.${randomValue}`;
}

function persistValue(cookieName: string, storageKey: string, value: string) {
  writeCookie(cookieName, value);
  writeStorage(storageKey, value);
}

export function initializeMetaAttribution(): MetaAttribution {
  if (typeof window === 'undefined' || typeof document === 'undefined') return {};

  const cookieFbp = readCookie(FBP_COOKIE);
  const storedFbp = readStorage(FBP_STORAGE_KEY);
  const fbp = isMetaBrowserId(cookieFbp)
    ? cookieFbp
    : isMetaBrowserId(storedFbp)
      ? storedFbp
      : createRandomBrowserId();
  persistValue(FBP_COOKIE, FBP_STORAGE_KEY, fbp);

  const fbclid = getFbclid();
  const cookieFbc = readCookie(FBC_COOKIE);
  const storedFbc = readStorage(FBC_STORAGE_KEY);
  let fbc: string | undefined;

  if (fbclid) {
    const existingFbc = isMetaBrowserId(cookieFbc)
      ? cookieFbc
      : isMetaBrowserId(storedFbc)
        ? storedFbc
        : undefined;
    fbc = existingFbc?.endsWith(`.${fbclid}`)
      ? existingFbc
      : `fb.1.${Date.now()}.${fbclid}`;
  } else if (isMetaBrowserId(cookieFbc)) {
    fbc = cookieFbc;
  } else if (isMetaBrowserId(storedFbc)) {
    fbc = storedFbc;
  }

  if (fbc) persistValue(FBC_COOKIE, FBC_STORAGE_KEY, fbc);

  return { fbp, fbc };
}

export function fetchWithMetaAttribution(
  input: RequestInfo,
  init: RequestInit = {},
): Promise<Response> {
  const requestInit: RequestInit = {
    ...init,
    credentials: init.credentials || 'same-origin',
  };

  try {
    const payload = typeof init.body === 'string' ? JSON.parse(init.body) : {};
    requestInit.body = JSON.stringify({
      ...payload,
      ...initializeMetaAttribution(),
    });
  } catch (error) {
    console.warn('Meta attribution could not be added to the request:', error);
  }

  return fetch(input, requestInit);
}
